import { IRefIds } from 'constants/refs';
import { get, post, put } from 'services/request';
import { BaseModel, IModelOptions } from './BaseModel';
import { IItemId } from '../types/content';

export type IDialect = IItemId & {
  code: string;
  name: string;
  languageId: number;
};

export type IDialectEdit = IItemId & {
  code: string;
  ssmlCode: string;
  name: string;
  languageId: number;
  onlyTarget: boolean;
};

export default class DialectsManageModel extends BaseModel<IDialect, IDialectEdit> {
  constructor(options: IModelOptions) {
    super(options);
    this.ref = IRefIds.dialects;
    this.gridName = 'Dialects';
    this.getGrid();
    this.getGridColumns();
  }

  async getGrid() {
    await super.getGrid();
    this.gridData = await get<IDialect[]>(`dialects`);
    this.loadingGrid = false;
    this.render();
  }

  async getGridRow(itemId: number) {
    return await get<IDialect>(`dialects/${itemId}/grid-row`);
  }

  async reload() {
    await super.reload();
    await this.getGrid();
  }

  async save() {
    await super.save();
    const { id } = this.itemUpdated;
    if (id === this.NEW_CREATE_ID) {
      await this.createItem();
    } else {
      await this.updateItem();
    }
  }

  async new() {
    await super.new();
    const item = await get<IDialectEdit>(`dialects/new`);
    await this.setItem(item);
    this.render();
  }

  async createItem() {
    const item = this.itemUpdated as IDialectEdit;
    const result = await post<IItemId>(`dialects`, {
      language_id: item.languageId,
      code: item.code,
      ssml_code: item.ssmlCode,
      name: item.name,
      only_target: item.onlyTarget,
    });

    if (result.ok && result.data) {
      const newItemId = result.data.id;
      await Promise.all([this.updateGridRow(newItemId, 'add'), this.loadItemEdit(newItemId)]);
    }
  }

  async updateItem() {
    const item = this.itemUpdated as IDialectEdit;
    await put(`dialects/${item.id}`, {
      language_id: item.languageId,
      code: item.code,
      ssml_code: item.ssmlCode,
      name: item.name,
      only_target: item.onlyTarget,
    });
    await Promise.all([this.updateGridRow(item.id, 'update'), this.loadItemEdit(item.id)]);
  }

  async loadItemData(itemId: number) {
    return await get<IDialectEdit>(`dialects/${itemId}`);
  }
}
